import { Block, Flex, Icon, IconButton, Tooltip, Typography, theme } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useState } from 'react';

import { fetchGetBlob } from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../../redux/features/referenceBarUISlice.ts';
import { useAppDispatch } from '../../../redux/hooks.ts';
import { usePropertyImage } from '../../../utils/usePropertyImages.tsx';
import {
  PROPERTY_HEADER_IMAGE_HEIGHT,
  PROPERTY_HEADER_IMAGE_WIDTH,
} from '../../PropertyGrid/consts.ts';
import { UploadPropertyPhotoModal } from '../../UploadPropertyPhotoModal/UploadPropertyPhotoModal.tsx';

type ValuationTablePropertyHeaderImageProps = {
  hideActions: boolean;
  propertyId: string;
};

export const ValuationTablePropertyHeaderImage = ({
  hideActions,
  propertyId,
}: ValuationTablePropertyHeaderImageProps) => {
  const dispatch = useAppDispatch();
  const { fetcherOptions } = useToolkitContext();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const propertyImage = usePropertyImage(propertyId);
  const [propertyImageSrc, setPropertyImageSrc] = useState<string | undefined>(undefined);

  const propertyImageSrcId = propertyImage?.src;
  const propertyImageType = propertyImage?.type;

  useEffect(() => {
    if (!propertyImageSrcId || !propertyImageType) return undefined;

    let active = true;
    let objectUrl: string | undefined;

    if (propertyImageType === 'blobID') {
      fetchGetBlob({
        ...fetcherOptions,
        pathParams: { blobID: propertyImageSrcId },
      })
        .then((blob) => {
          if (!active) return;

          const file = new File([blob as BlobPart], 'propertyImage.png', {
            type: blob.type as string,
          });

          objectUrl = URL.createObjectURL(file);
          setPropertyImageSrc(objectUrl);
        })
        .catch(console.error);
    } else {
      setPropertyImageSrc(propertyImageSrcId);
    }

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [propertyImageSrcId, propertyImageType, fetcherOptions]);

  const handleImageClick = useMemo(
    () => () => {
      if (hideActions) return;

      if (propertyImage?.source === 'mls-images' || propertyImage?.source === 'mls-street-view') {
        dispatch(setSelectedDataSheet(propertyImage.source));
        dispatch(setReferenceViewOpen(true));
        return;
      }

      setIsUploadModalOpen(true);
    },
    [dispatch, hideActions, propertyImage],
  );

  return (
    <Block position="relative">
      <Tooltip openDelay={500} label={propertyImage?.tooltip ?? ''} isDisabled={hideActions}>
        <Block
          height={PROPERTY_HEADER_IMAGE_HEIGHT}
          width={PROPERTY_HEADER_IMAGE_WIDTH}
          minWidth="81px"
          onClick={handleImageClick}
          cursor={hideActions ? 'default' : 'pointer'}
          position="relative"
        >
          <Flex
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
            backgroundColor="strokeDefault"
            overflow="hidden"
          >
            {propertyImage ? (
              <img
                alt="Property"
                height="100%"
                width="100%"
                src={propertyImageSrc}
                style={{ height: '100%', width: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100%"
                width="100%"
                paddingTop="1rem"
                gap="4px"
                backgroundColor="strokeDefault"
              >
                <Typography
                  variant="body2"
                  color="textTertiary"
                  __dangerDoNotUseSx={{ fontStyle: 'italic' }}
                >
                  Photo Unavailable
                </Typography>
                <Icon icon="image-add" color="buttonDefault" size="lg" />
                <Typography variant="body2" color="buttonDefault">
                  Upload Photo
                </Typography>
              </Flex>
            )}
          </Flex>
        </Block>
      </Tooltip>
      {!hideActions && (
        <Tooltip openDelay={500} label="Edit Photo">
          <IconButton
            ariaLabel="Edit Property Photo"
            title="Edit Property Photo"
            variant="quaternary"
            icon="pencil"
            iconColor="textPrimary"
            iconSize="md"
            size="xs"
            isRound
            __dangerDoNotUseSx={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: theme.colors.surfaceAccent5,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsUploadModalOpen(true);
            }}
          />
        </Tooltip>
      )}
      <UploadPropertyPhotoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        propertyId={propertyId}
      />
    </Block>
  );
};
